/* eslint-disable prefer-destructuring */
import { calcHasReviewSettingsAccess } from 'Deer/constants/L3RenderMap';
import { L3_PATHS } from 'Deer/constants/routes';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { getUserAccess, getUserL2Access } from 'Deer/helpers/getUserAccess';
import { openPanel } from 'components/RabbitInitiativeCreationPanel/InitiativeCreationPanelSlice';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { CREATE_INITIATIVE_PRIVILEGES } from 'components/hooks/useEmployeeGoalsPrivileges';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { REVIEW_CYCLE_STEPS_V1 } from 'reducers/reviewCreationSlice';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { okrsPageSelector } from 'selectors/okrsPage';
import store from 'store';
import {
  check1on1V2Enabled,
  checkDenyPublicCFVisibilityEnabled,
  checkSimplifiedReviewCreationEnabled,
} from 'utils/helpers/featureFlags';
import { checkUserIsSelf } from 'utils/helpers/rolesAccess';

import {
  ChangeWeightage,
  Goals,
  Initiatives,
  OneOnOne,
  Praise,
  RequestFeedback,
  ReviewsIcon,
  ShareFeedback,
} from 'assets/assetsV1/iconComponents';

import { OKR_TYPES } from 'constants/ProjectConstants';
import {
  sendClickedOnQuickActionsEvent,
  sendCreatedKREvent,
  sendOpenedInitiativePanel,
  sendOpenedKRPanelEvent,
  sendPageViewEvent,
} from 'constants/mixpanelHelper';

export const ACTIONS_LIST = [
  {
    id: 'create-goal',
    displayName: 'Add a goal',
    Icon: Goals,
    isActionVisible: () => getUserL2Access().hasGoalAccess,
    onClick: ({ history, location, dispatch }) => {
      const { selectedTimelines } = store.getState(okrsPageSelector);
      sendOpenedKRPanelEvent({
        eventProperties: { page: 'Home', source: 'Quick Actions', type: 'Add KR Panel' },
      });
      sendPageViewEvent({
        eventProperties: {
          page: 'Add KR Panel',
          type: 'Panel',
          source: 'Quick Actions',
        },
      });
      dispatch(
        openCreateOKRPanel({
          selectedObjective: null,
          selectedTimeBoundPeriod: selectedTimelines?.[0],
          okrPanelType: OKR_TYPES.KEY_RESULT,
          onKeyResultCreation: (okrDetails) => {
            sendCreatedKREvent({
              eventProperties: { goalId: okrDetails?.uuid, source: 'Quick Actions', page: 'Home' },
            });
          },
          onObjectiveCreation: () => {},
          history,
          location,
        })
      );
    },
  },
  {
    id: 'manage-goal-weightage',
    displayName: 'Add goal weightages',
    Icon: ChangeWeightage,
    isActionVisible: () => {
      if (getUserL2Access().hasReviewAccess && getUserL2Access().hasGoalAccess) return true;
      return false;
    },
    onClick: ({ openMyWeightageSidePanel }) => {
      sendClickedOnQuickActionsEvent({
        eventProperties: {
          action: 'Change Weightage',
        },
      });
      openMyWeightageSidePanel();
    },
    hideOnProfilePage: ({
      empId,
      isLoggedInPersonManagerOfManagerOfProfilePerson,
      isProfileEmployeeDirectReportee,
    }) => {
      if (!getUserL2Access().hasGoalAccess) return true;
      return !(
        checkUserIsSelf(empId) ||
        getUserAccess().isReviewAdmin ||
        isLoggedInPersonManagerOfManagerOfProfilePerson ||
        isProfileEmployeeDirectReportee
      );
    },
  },
  {
    id: 'create-review-cycle',
    displayName: 'Launch a review',
    Icon: ReviewsIcon,
    isActionVisible: () => calcHasReviewSettingsAccess(),
    onClick: ({ history }) => {
      if (checkSimplifiedReviewCreationEnabled()) {
        history.push(L3_PATHS.company.review.reviewCreation.replace(':reviewId', 'presets'));
        return;
      }
      const reviewCycleCreationRoute = staticRoutesWithCustomSidebar.reviewCycleCreation;

      const route = reviewCycleCreationRoute.replace(':formId', 'new').replace(':step', REVIEW_CYCLE_STEPS_V1.NAME);
      history.push(route);
    },
  },
  {
    id: 'add-1on1',
    displayName: 'Add a 1:1',
    Icon: OneOnOne,
    isActionVisible: () => getUserL2Access().hasOneOnOneAccess,
    onClick: ({ dispatch, setIsOneOnOnePanelOpen = () => {} }) => {
      // eslint-disable-next-line no-unused-expressions
      check1on1V2Enabled()
        ? dispatch(
            // @ts-expect-error ts issue
            openCreateOneOnOneDrawer({
              excludedParticipantIds: [],
            })
          )
        : setIsOneOnOnePanelOpen(true);
    },
  },
  {
    id: 'share-public-praise',
    displayName: 'Share public praise',
    Icon: Praise,
    isActionVisible: () => getUserL2Access().hasFeedbackAccess && !checkDenyPublicCFVisibilityEnabled(),
    onClick: ({ history, sharePublicPraiseParams }) => {
      history.push({
        search: sharePublicPraiseParams,
      });
    },
  },
  {
    id: 'share-feedback',
    displayName: 'Share feedback',
    Icon: ShareFeedback,
    isActionVisible: () => getUserL2Access().hasFeedbackAccess,
    onClick: ({ history, shareFeedbackParams }) => {
      history.push({
        search: shareFeedbackParams,
      });
    },
  },
  {
    id: 'request-feedback',
    displayName: 'Request feedback',
    Icon: RequestFeedback,
    isActionVisible: () => getUserL2Access().hasFeedbackAccess,
    onClick: ({ history, requestFeedbackParams }) => {
      history.push({
        search: requestFeedbackParams,
      });
    },
  },
  {
    id: 'add-initiative',
    displayName: 'Add an initiative',
    Icon: Initiatives,
    isActionVisible: () => {
      const accessPrivilegesList = store.getState().employeeGoalsAccessPrivileges ?? [];
      const canCreateInitiativePrivilege = accessPrivilegesList.includes(
        CREATE_INITIATIVE_PRIVILEGES.CREATE_INITIATIVES
      );
      return getUserL2Access().hasInitiativeAccess && canCreateInitiativePrivilege;
    },
    onClick: ({ employee, dispatch }) => {
      const updatedToIds = [
        {
          uuid: employee.id,
          ownerType: 'OWNER',
          metadata: { displayName: employee.displayName },
          profile: { profilePhoto: employee.profilePhotoSrc },
        },
      ];

      // @ts-expect-error ts issue
      dispatch(closeSecondaryPanel()); /// // WILL CLOSE SECONDARY DRAWER ON INITIAL OPENING ON ADD NEW INITIATIVE PANEL
      sendOpenedInitiativePanel({
        eventProperties: {
          page: 'Home',
          source: 'Quick Actions',
        },
      });
      dispatch(
        openPanel({
          toIds: updatedToIds,
          page: 'Home',
          source: 'Quick Actions',
        })
      );
    },
  },
];
